import {
  BodyText,
  Button,
  Icon,
  IconSet,
  ProgressBar,
} from '@postscript/components';
import React from 'react';
import styled from 'styled-components';

interface LaunchpadProgressCardProps {
  title: string;
  subtitle: string;
  icon: () => JSX.Element;
  completedSteps: number;
  totalSteps: number;
  isComplete?: boolean;
  onClick?: () => void;
}

const StyledCard = styled.div<{ $isComplete: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  padding: var(--spacing-2);
  border: 1px solid
    ${({ $isComplete }) =>
      $isComplete
        ? 'var(--border-color-extra-dim)'
        : 'var(--border-color-dim)'};
  border-radius: var(--border-radius-small);
  background: var(--surface-bkg-color);
  overflow: hidden;
  width: 100%;
  cursor: pointer;
  transition: border-color var(--hover-transition);

  ${({ $isComplete }) =>
    !$isComplete &&
    `
    &:hover {
      border-color: var(--link-color-hover);
    }
    &:active {
      border-color: var(--link-color-active);
    }
  `}
`;

const StyledHeader = styled.div`
  display: flex;
  gap: var(--spacing-1);
  align-items: flex-start;
  width: 100%;
`;

const StyledIconWrapper = styled.div`
  flex-shrink: 0;
  width: var(--spacing-4);
  height: var(--spacing-4);
`;

const StyledTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const StyledTitle = styled(BodyText)<{ $isComplete?: boolean }>`
  color: ${({ $isComplete }) =>
    $isComplete ? 'var(--text-color-dim)' : 'var(--text-color)'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledAction = styled.div`
  display: flex;
  align-items: center;
  height: var(--spacing-4);
  flex-shrink: 0;
`;

const LaunchpadProgressCard: React.FC<LaunchpadProgressCardProps> = ({
  title,
  subtitle,
  icon,
  completedSteps,
  totalSteps,
  isComplete = false,
  onClick,
}) => {
  return (
    <StyledCard
      $isComplete={isComplete}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <StyledHeader>
        <StyledIconWrapper>
          <Icon
            component={isComplete ? IconSet.CheckCircleFilled : icon}
            color={isComplete ? 'var(--highlight-color)' : undefined}
            size={24}
          />
        </StyledIconWrapper>
        <StyledTextGroup>
          <StyledTitle size="small" bold $isComplete={isComplete}>
            {title}
          </StyledTitle>
          <BodyText size="x-small">{subtitle}</BodyText>
        </StyledTextGroup>
        {!isComplete && (
          <StyledAction>
            <Button
              variant="text"
              size="small"
              icon={IconSet.ArrowRight}
              onClick={(e) => {
                e.stopPropagation();
                onClick?.();
              }}
              aria-label="Open"
            />
          </StyledAction>
        )}
      </StyledHeader>

      {!isComplete && (
        <ProgressBar step={completedSteps + 1} totalSteps={totalSteps} />
      )}
    </StyledCard>
  );
};

export default LaunchpadProgressCard;
